import React, { useState } from 'react';
import { BatchRecord } from '../../../types/batchRecord';
import { BatchPan } from '../../../types/batchPan';

interface ProcessingStep {
  name: string;
  targetTempField: keyof BatchRecord;
  targetTempMaxField?: keyof BatchRecord;
  targetTimeField: keyof BatchRecord;
  actualTempKey: keyof BatchPan;
  actualTimeKey: keyof BatchPan;
  ccpAchievedKey: keyof BatchPan;
  correctiveActionKey: keyof BatchPan;
  checkedByKey: keyof BatchPan;
}

interface ProcessingParametersTableProps {
  record: BatchRecord;
  pans: BatchPan[];
  onPanUpdate: (pan: BatchPan) => void;
}

const steps: ProcessingStep[] = [
  {
    name: 'Baking',
    targetTempField: 'bakingTargetTemp',
    targetTempMaxField: 'bakingTargetTempMax',
    targetTimeField: 'bakingTargetTime',
    actualTempKey: 'bakingActualTemp',
    actualTimeKey: 'bakingActualTime',
    ccpAchievedKey: 'bakingCcpAchieved',
    correctiveActionKey: 'bakingCorrectiveAction',
    checkedByKey: 'bakingCheckedBy'
  },
  {
    name: 'Cooling 1',
    targetTempField: 'cooling1TargetTemp',
    targetTimeField: 'cooling1TargetTime',
    actualTempKey: 'cooling1ActualTemp',
    actualTimeKey: 'cooling1ActualTime',
    ccpAchievedKey: 'cooling1CcpAchieved',
    correctiveActionKey: 'cooling1CorrectiveAction',
    checkedByKey: 'cooling1CheckedBy'
  },
  {
    name: 'Cooling 2',
    targetTempField: 'cooling2TargetTemp',
    targetTimeField: 'cooling2TargetTime',
    actualTempKey: 'cooling2ActualTemp',
    actualTimeKey: 'cooling2ActualTime',
    ccpAchievedKey: 'cooling2CcpAchieved',
    correctiveActionKey: 'cooling2CorrectiveAction',
    checkedByKey: 'cooling2CheckedBy'
  },
  {
    name: 'Freezing',
    targetTempField: 'freezingTargetTemp',
    targetTimeField: 'freezingTargetTime',
    actualTempKey: 'freezingActualTemp',
    actualTimeKey: 'freezingActualTime',
    ccpAchievedKey: 'freezingCcpAchieved',
    correctiveActionKey: 'freezingCorrectiveAction',
    checkedByKey: 'freezingCheckedBy'
  }
];

const stageBadgeColors: Record<string, { bg: string; text: string }> = {
  baking: { bg: '#FFF3E0', text: '#E65100' },
  cooling1: { bg: '#E3F2FD', text: '#0D47A1' },
  cooling2: { bg: '#E3F2FD', text: '#0D47A1' },
  freezing: { bg: '#E8EAF6', text: '#283593' },
  complete: { bg: '#E8F5E9', text: '#2E7D32' }
};

export const ProcessingParametersTable: React.FC<ProcessingParametersTableProps> = ({
  record,
  pans,
  onPanUpdate
}) => {
  const [expandedPanId, setExpandedPanId] = useState<string | null>(null);
  const [editedPans, setEditedPans] = useState<Record<string, BatchPan>>({});
  const [dirtyPanIds, setDirtyPanIds] = useState<Set<string>>(new Set());

  const getEditedPan = (pan: BatchPan): BatchPan => editedPans[pan.id] || pan;

  const handlePanFieldChange = (pan: BatchPan, field: keyof BatchPan, value: any) => {
    const current = getEditedPan(pan);
    const updated = { ...current, [field]: value };
    setEditedPans(prev => ({ ...prev, [pan.id]: updated }));
    setDirtyPanIds(prev => new Set(prev).add(pan.id));
  };

  const handleSavePan = (pan: BatchPan) => {
    const edited = getEditedPan(pan);
    onPanUpdate(edited);
    setDirtyPanIds(prev => {
      const next = new Set(prev);
      next.delete(pan.id);
      return next;
    });
  };

  const handleCancelPan = (pan: BatchPan) => {
    setEditedPans(prev => {
      const next = { ...prev };
      delete next[pan.id];
      return next;
    });
    setDirtyPanIds(prev => {
      const next = new Set(prev);
      next.delete(pan.id);
      return next;
    });
  };

  const inputStyle: React.CSSProperties = {
    padding: '6px 8px',
    border: '1px solid #ddd',
    borderRadius: '4px',
    fontSize: '13px',
    width: '100%',
    boxSizing: 'border-box'
  };

  const buttonStyle: React.CSSProperties = {
    padding: '8px 16px',
    borderRadius: '4px',
    border: 'none',
    fontWeight: '500',
    cursor: 'pointer',
    fontSize: '14px',
    transition: 'background-color 0.2s'
  };

  const sortedPans = [...pans].sort((a, b) => a.panNumber - b.panNumber);

  return (
    <div>
      {sortedPans.map(pan => {
        const isExpanded = expandedPanId === pan.id;
        const edited = getEditedPan(pan);
        const isDirty = dirtyPanIds.has(pan.id);
        const badge = stageBadgeColors[pan.stage] || stageBadgeColors.baking;

        return (
          <div
            key={pan.id}
            style={{
              border: '1px solid #e0e0e0',
              borderRadius: '4px',
              marginBottom: '8px',
              overflow: 'hidden'
            }}
          >
            {/* Pan Header */}
            <div
              onClick={() => setExpandedPanId(isExpanded ? null : pan.id)}
              style={{
                padding: '10px 16px',
                backgroundColor: '#f8f9fa',
                borderBottom: isExpanded ? '1px solid #e0e0e0' : 'none',
                cursor: 'pointer',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                transition: 'background-color 0.2s'
              }}
              onMouseEnter={e => (e.currentTarget.style.backgroundColor = '#eeeeee')}
              onMouseLeave={e => (e.currentTarget.style.backgroundColor = '#f8f9fa')}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <span style={{ fontWeight: '600', fontSize: '14px', color: '#333' }}>
                  Pan #{pan.panNumber}
                </span>
                <span
                  style={{
                    padding: '2px 8px',
                    borderRadius: '12px',
                    fontSize: '11px',
                    fontWeight: '600',
                    backgroundColor: badge.bg,
                    color: badge.text,
                    textTransform: 'capitalize'
                  }}
                >
                  {pan.stage}
                </span>
              </div>
              <span
                style={{
                  fontSize: '0.9rem',
                  color: '#666',
                  transition: 'transform 0.2s ease-in-out',
                  transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)',
                  display: 'inline-block'
                }}
              >
                ▶
              </span>
            </div>

            {/* Pan Processing Table */}
            {isExpanded && (
              <div style={{ padding: '16px' }}>
                <div style={{ overflowX: 'auto' }}>
                  <table
                    style={{
                      width: '100%',
                      borderCollapse: 'collapse',
                      fontSize: '13px',
                      backgroundColor: 'white'
                    }}
                  >
                    <thead>
                      <tr>
                        {['Step', 'Target Temp/Time', 'Actual Temp (°F)', 'Actual Time (min)', 'CCP Achieved', 'Corrective Action', 'Checked By'].map(
                          header => (
                            <th
                              key={header}
                              style={{
                                padding: '12px 8px',
                                backgroundColor: '#f8f9fa',
                                fontWeight: '600',
                                textAlign: 'left',
                                borderBottom: '2px solid #e0e0e0',
                                textTransform: 'uppercase',
                                fontSize: '11px',
                                letterSpacing: '0.5px',
                                color: '#555',
                                minWidth: header === 'Corrective Action' ? '150px' : header === 'Target Temp/Time' ? '120px' : '100px'
                              }}
                            >
                              {header}
                            </th>
                          )
                        )}
                      </tr>
                    </thead>
                    <tbody>
                      {steps.map((step, index) => {
                        const targetTemp = record[step.targetTempField] as number | undefined;
                        const targetTempMax = step.targetTempMaxField ? record[step.targetTempMaxField] as number | undefined : undefined;
                        const targetTime = record[step.targetTimeField] as number | undefined;
                        const actualTemp = edited[step.actualTempKey] as number | undefined;
                        const actualTime = edited[step.actualTimeKey] as number | undefined;
                        const ccpAchieved = edited[step.ccpAchievedKey] as boolean | undefined;
                        const correctiveAction = edited[step.correctiveActionKey] as string | undefined;
                        const checkedBy = edited[step.checkedByKey] as string | undefined;

                        return (
                          <tr key={step.name}>
                            <td
                              style={{
                                padding: '12px 8px',
                                borderBottom: index < steps.length - 1 ? '1px solid #eee' : 'none',
                                fontWeight: '500',
                                color: '#333'
                              }}
                            >
                              {step.name}
                            </td>
                            <td
                              style={{
                                padding: '12px 8px',
                                borderBottom: index < steps.length - 1 ? '1px solid #eee' : 'none',
                                color: '#666',
                                fontSize: '13px'
                              }}
                            >
                              {targetTemp ? (targetTempMax ? `${targetTemp}–${targetTempMax}°F` : `${targetTemp}°F`) : '-'} / {targetTime ? `${targetTime} min` : '-'}
                            </td>
                            <td
                              style={{
                                padding: '12px 8px',
                                borderBottom: index < steps.length - 1 ? '1px solid #eee' : 'none'
                              }}
                            >
                              <input
                                type="number"
                                step="0.1"
                                value={actualTemp ?? ''}
                                onChange={e =>
                                  handlePanFieldChange(
                                    pan,
                                    step.actualTempKey,
                                    e.target.value ? parseFloat(e.target.value) : undefined
                                  )
                                }
                                style={inputStyle}
                                placeholder="Temp"
                              />
                            </td>
                            <td
                              style={{
                                padding: '12px 8px',
                                borderBottom: index < steps.length - 1 ? '1px solid #eee' : 'none'
                              }}
                            >
                              <input
                                type="number"
                                value={actualTime ?? ''}
                                onChange={e =>
                                  handlePanFieldChange(
                                    pan,
                                    step.actualTimeKey,
                                    e.target.value ? parseInt(e.target.value) : undefined
                                  )
                                }
                                style={inputStyle}
                                placeholder="Time"
                              />
                            </td>
                            <td
                              style={{
                                padding: '12px 8px',
                                borderBottom: index < steps.length - 1 ? '1px solid #eee' : 'none'
                              }}
                            >
                              <select
                                value={ccpAchieved === undefined ? '' : ccpAchieved ? 'yes' : 'no'}
                                onChange={e =>
                                  handlePanFieldChange(
                                    pan,
                                    step.ccpAchievedKey,
                                    e.target.value === '' ? undefined : e.target.value === 'yes'
                                  )
                                }
                                style={inputStyle}
                              >
                                <option value="">Select...</option>
                                <option value="yes">Yes</option>
                                <option value="no">No</option>
                              </select>
                            </td>
                            <td
                              style={{
                                padding: '12px 8px',
                                borderBottom: index < steps.length - 1 ? '1px solid #eee' : 'none'
                              }}
                            >
                              <input
                                type="text"
                                value={correctiveAction ?? ''}
                                onChange={e =>
                                  handlePanFieldChange(
                                    pan,
                                    step.correctiveActionKey,
                                    e.target.value || undefined
                                  )
                                }
                                style={inputStyle}
                                placeholder="Optional"
                              />
                            </td>
                            <td
                              style={{
                                padding: '12px 8px',
                                borderBottom: index < steps.length - 1 ? '1px solid #eee' : 'none'
                              }}
                            >
                              <input
                                type="text"
                                value={checkedBy ?? ''}
                                onChange={e =>
                                  handlePanFieldChange(
                                    pan,
                                    step.checkedByKey,
                                    e.target.value || undefined
                                  )
                                }
                                style={inputStyle}
                                placeholder="Required"
                              />
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                {isDirty && (
                  <div style={{ marginTop: '16px', display: 'flex', gap: '8px' }}>
                    <button
                      onClick={() => handleSavePan(pan)}
                      style={{
                        ...buttonStyle,
                        backgroundColor: '#4CAF50',
                        color: 'white'
                      }}
                      onMouseEnter={e => (e.currentTarget.style.backgroundColor = '#45a049')}
                      onMouseLeave={e => (e.currentTarget.style.backgroundColor = '#4CAF50')}
                    >
                      Save Pan #{pan.panNumber}
                    </button>
                    <button
                      onClick={() => handleCancelPan(pan)}
                      style={{
                        ...buttonStyle,
                        backgroundColor: '#757575',
                        color: 'white'
                      }}
                      onMouseEnter={e => (e.currentTarget.style.backgroundColor = '#616161')}
                      onMouseLeave={e => (e.currentTarget.style.backgroundColor = '#757575')}
                    >
                      Cancel
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })}

      {sortedPans.length === 0 && (
        <div style={{ padding: '20px', textAlign: 'center', color: '#999', fontSize: '14px' }}>
          No pans recorded for this batch.
        </div>
      )}
    </div>
  );
};
